import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Branch,
  ImportResult,
  InventoryItem,
  ProcedureCategory,
  ProcedureCode,
  ProcedureMaterial,
} from "@dentacore/shared-types";

import { listBranches } from "../api/branches";
import {
  acceptStarterLibrary,
  copyCatalogueFrom,
  createProcedureCode,
  importCatalogue,
  listProcedureCodes,
  setProcedureVisits,
  updateProcedureCode,
} from "../api/catalogue";
import {
  addProcedureMaterial,
  editProcedureMaterial,
  listInventory,
  listProcedureMaterials,
  removeProcedureMaterial,
} from "../api/inventory";
import { listProcedureCategories } from "../api/procedure-categories";
import { useActiveBranch } from "../api/use-active-branch";
import { ShelfList } from "../inventory/shelf-list";
import type { ShelfRow } from "../inventory/shelf-list";
import { NoScope, scopeProblem, useScopedResource } from "../settings/pane";
import { Toast } from "../settings/toast";
import { useWriter } from "../settings/use-writer";
import { Modal } from "../ui/modal";
import type { Follow } from "../ui/paging";
import { ProcedureDialog } from "./procedure-dialog";
import type { ProcedureDialogMode } from "./procedure-dialog";
import { formatMinor, plural } from "./procedure-form";
import { CategoriesDialog } from "./categories-dialog";
import { ProcedureMaterials } from "./procedure-materials";
import { ProcedurePanel } from "./procedure-panel";
import styles from "./procedures.module.css";
import { atLocation } from "../api/client";
import { locate } from "../api/located";
import {
  Badges,
  CopySwitch,
  LocationField,
  across,
  groupByName,
  useShown,
} from "../shell/locations";
import type { Located } from "../shell/locations";
import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { useBranchCurrency } from "../api/use-branch-currency";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";

/**
 * The Treatment plans screen: this location's procedures, their prices, and
 * the visits each one takes (PV1, PV7) — **laid out calm** (TR1–TR9,
 * 2026-09-27).
 *
 * A row is the procedure and its price, with a quiet "3 visits" beside a course
 * (TR2). Everything else — the code, where it is done, each visit, Edit,
 * Duplicate, Retire — opens in a panel beside the list (TR3); adding and
 * editing happen in one dialog (TR4). **Nothing the page did before was
 * removed** (CS1); the prototype's table in DECISIONS.md maps where each went.
 *
 * **Each location owns its own list outright** (ADR-0008), so the page follows
 * the location switcher and names the location beside its title (TR8).
 *
 * **Everyone reads it; only an admin changes it** (PL2, TR9). Reading the list
 * was never admin-only on the server — the booking form uses the same endpoint —
 * and every write stays `@Roles("ADMIN")` there. `canEdit` only decides which
 * controls are drawn; the server is still the thing that refuses.
 */
export function ProceduresPage(): React.JSX.Element {
  const can = useCan();
  const canEdit = can("procedures.manage");
  /**
   * **A procedure's tray is Supply's** (MS-D2) — without it the panel has no
   * materials at all, and nothing asks the server for them. **Its cost and profit
   * need Accounting and Supply both** (MS-D6): profit is price less materials, so
   * it is never shown without the materials it subtracts — and still only to
   * whoever sees Accounting (MC-D7).
   */
  const { has } = useModules();
  const hasSupply = has("SUPPLY");
  const canSeeMoney = can("accounting.view") && has("ACCOUNTING") && hasSupply;
  const currency = useBranchCurrency();
  const activeBranchId = useActiveBranch();
  const { busy, toast, dismiss, fail, run } = useWriter();

  const [branches, setBranches] = useState<readonly Branch[] | null>(null);
  const [query, setQuery] = useState("");
  const [retiredView, setRetiredView] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [follow, setFollow] = useState<Follow | null>(null);
  const [dialog, setDialog] = useState<ProcedureDialogMode | null>(null);
  /** The clinic's categories, open in their own dialog (PG-D2). */
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  /** The last import's report, open until dismissed — it names the rows to fix. */
  const [imported, setImported] = useState<ImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /** The clinic's procedure categories (PG-D1), for the form and the panel. */
  const [categories, setCategories] = useState<readonly ProcedureCategory[] | null>(null);
  /** The open procedure's tray (MC-D1) — null while it is being read. */
  const [materials, setMaterials] = useState<readonly ProcedureMaterial[] | null>(null);
  const [editingMaterials, setEditingMaterials] = useState(false);
  /** This location's stock, read the first time someone starts editing a tray. */
  const [stock, setStock] = useState<readonly InventoryItem[] | null>(null);
  /**
   * A procedure the quick ring asked to open with its tray in editing
   * (STORY-221). Opening another procedure resets editing, so the request is
   * carried across that reset rather than set beside it.
   */
  const editMaterialsOf = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    listBranches(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setBranches(rows);
      })
      // Silent for the reason `SettingsLayout` gives: the catalogue read below
      // reports its own failure, and a second toast would say it twice.
      .catch(() => {
        if (!controller.signal.aborted) setBranches([]);
      });

    return () => controller.abort();
  }, []);

  // Another location is another list: nothing open carries over to it.
  useEffect(() => {
    setOpenId(null);
    setRetiredView(false);
    setQuery("");
    setStock(null);
  }, [activeBranchId]);

  // One list for the whole clinic (PG-D2), so read once, not per location.
  const reloadCategories = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      listProcedureCategories(signal)
        .then((rows) => {
          if (signal?.aborted !== true) setCategories(rows);
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setCategories([]);
          fail(cause instanceof Error ? cause.message : "Could not read the categories.");
        }),
    [fail],
  );

  useEffect(() => {
    const controller = new AbortController();
    void reloadCategories(controller.signal);
    return () => controller.abort();
  }, [reloadCategories]);

  // The open procedure's tray, read whenever another one opens. Editing never
  // carries over from one procedure to the next.
  useEffect(() => {
    // Opened by the quick ring's *Edit materials* (STORY-221): open in editing.
    const editNow = openId !== null && editMaterialsOf.current === openId;
    editMaterialsOf.current = null;
    setMaterials(null);
    setEditingMaterials(editNow);
    if (openId === null || !hasSupply) return;

    const controller = new AbortController();
    listProcedureMaterials(openId, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setMaterials(rows);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setMaterials([]);
        fail(cause instanceof Error ? cause.message : "Its materials could not be read.");
      });

    return () => controller.abort();
  }, [openId, fail, hasSupply]);

  // What can be added to a tray: this location's current stock, read once,
  // and only when somebody actually starts editing one.
  useEffect(() => {
    if (!editingMaterials || stock !== null) return;

    const controller = new AbortController();
    listInventory(false, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setStock(rows);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setStock([]);
        fail(cause instanceof Error ? cause.message : "This location's stock could not be read.");
      });

    return () => controller.abort();
  }, [editingMaterials, stock, fail]);

  /**
   * One write to the open tray. Every write returns the whole new tray, so the
   * section is refreshed from the response and nothing else is re-read.
   */
  const writeMaterials = (
    write: () => Promise<readonly ProcedureMaterial[]>,
    done: string,
  ): Promise<boolean> =>
    run(
      async () => {
        setMaterials(await write());
      },
      () => Promise.resolve(),
      done,
    );

  const branch = branches?.find((row) => row.id === activeBranchId) ?? null;
  const problem = scopeProblem(branches, branch);

  // Retired rows only for whoever can bring one back. A retired procedure
  // cannot be picked anywhere, so for everybody else it is noise.
  // The locations ticked in the top bar: one list for all of them.
  const places = useShown();
  const [addAt, setAddAt] = useState<string | null>(null);
  const load = useCallback(
    (signal?: AbortSignal): Promise<readonly ProcedureCode[]> =>
      places.many
        ? across(places.ids, () => listProcedureCodes(canEdit, signal))
        : listProcedureCodes(canEdit, signal),
    [canEdit, places.key],
  );

  const { data: codes, reload } = useScopedResource(
    load,
    "Could not read this location's procedures.",
    fail,
    problem === null,
    activeBranchId,
  );

  /** The same procedure at each location shown, by the id of any copy. */
  const copiesOf = useMemo(() => {
    const map = new Map<string, Located<ProcedureCode>[]>();
    if (!places.many) return map;
    for (const group of groupByName(
      (codes ?? []) as Located<ProcedureCode>[],
      (row) => row.label,
    )) {
      for (const copy of group.copies) map.set(copy.id, group.copies);
    }
    return map;
  }, [codes, places.many]);
  /** One row per procedure: the first copy of each name. */
  const firsts = (list: readonly ProcedureCode[]): ProcedureCode[] =>
    places.many ? list.filter((row) => copiesOf.get(row.id)?.[0]?.id === row.id) : [...list];

  const active = useMemo(
    () => firsts((codes ?? []).filter((row) => row.isActive)),
    [codes, copiesOf],
  );
  const retiredCount = (codes ?? []).length - active.length;

  /**
   * What the list shows, in the server's order. **Search matches the name or
   * the code** (TR7) — a clinic that knows its procedures by code types the
   * code. The retired view is every retired procedure, unsearched.
   */
  const shown = useMemo(() => {
    if (retiredView) return (codes ?? []).filter((row) => !row.isActive);
    const needle = query.trim().toLocaleLowerCase();
    if (needle === "") return active;
    return active.filter(
      (row) =>
        row.label.toLocaleLowerCase().includes(needle) ||
        row.code.toLocaleLowerCase().includes(needle),
    );
  }, [codes, active, query, retiredView]);

  const open = openId === null ? null : ((codes ?? []).find((row) => row.id === openId) ?? null);

  /** Open a procedure beside the list, and turn the list to its page. */
  const reveal = (id: string): void => {
    setOpenId(id);
    setFollow({ key: id });
  };

  const rows: ShelfRow[] = shown.map((row) => {
    const copies = copiesOf.get(row.id) ?? [];
    const on = row.id === openId || copies.some((copy) => copy.id === openId);
    const prices = copies.map((copy) => copy.defaultPriceCents);
    const low = Math.min(...prices);
    const high = Math.max(...prices);
    const visits = row.visits.length;
    return {
      key: row.id,
      node: (
        <button
          type="button"
          className={[styles.row, on ? styles.rowOn : "", row.isActive ? "" : styles.rowRetired]
            .filter(Boolean)
            .join(" ")}
          aria-current={on ? "true" : undefined}
          onClick={() => setOpenId(on ? null : row.id)}
          data-card=""
          data-testid="proc-row"
          // The quick ring's thing (STORY-221): its ring is `plans.proc`, by `data-procedure`.
          data-ring="plans.proc"
          data-procedure={row.id}
        >
          <span className={styles.rowName}>
            <span className={styles.rowLabel} data-testid="proc-row-label">
              {row.label}
            </span>
            <Badges ats={copies.map((copy) => copy.at)} shown={places} />
            {/* A course says so, quietly (TR2); one visit, which is most, says nothing. */}
            {visits > 0 && (
              <span className={styles.tag} data-testid="proc-row-visit-count">
                {visits} visits
              </span>
            )}
          </span>
          <span className={styles.rowPrice} data-testid="proc-row-price">
            {copies.length > 1 && low !== high
              ? `${formatMinor(low, currency)}–${formatMinor(high, currency)}`
              : formatMinor(row.defaultPriceCents, currency)}
          </span>
        </button>
      ),
    };
  });

  /**
   * *Retire* — the panel's ⋯ entry and the quick ring's, one write. Gone from
   * the working list, because that is what retiring is for — so its panel, if
   * open, goes with it.
   */
  const retire = (target: ProcedureCode): void => {
    void run(() => updateProcedureCode(target.id, { isActive: false }), reload, "Retired.").then(
      (landed) => {
        if (landed) setOpenId((was) => (was === target.id ? null : was));
      },
    );
  };

  // ── The quick ring (STORY-221, QR7–QR9) ─────────────────────────────────
  // Only what this screen already does (QR8): each action opens the same
  // dialog or panel state its button does, or runs the same write, and is
  // offered only where that button is drawn — `procedures.manage`, an active
  // procedure, and Supply for the tray (MS-D2).

  useRingProvider("plans.proc", (element) => {
    const id = element.dataset.procedure;
    const target = (codes ?? []).find((row) => row.id === id);
    if (target === undefined) return null;
    const live = target.isActive && problem === null;
    return {
      ring: "plans.proc",
      title: target.label,
      meta: target.isActive
        ? `${plural(Math.max(1, target.visits.length), "visit")} · ${formatMinor(target.defaultPriceCents, currency)}`
        : "Retired",
      subject: { type: "procedure", id: target.id },
      actions: [
        live && {
          key: "edit",
          label: "Price and visits",
          icon: RING_ICONS.pencil,
          hint: "Edits price, visits, minutes",
          needs: ["procedures.manage"],
          run: () => setDialog({ kind: "edit", code: target }),
        },
        live &&
          hasSupply && {
            key: "mat",
            label: "Edit materials",
            icon: RING_ICONS.box,
            hint: "What its tray uses",
            needs: ["procedures.manage"],
            module: "SUPPLY",
            run: () => {
              if (openId === target.id) {
                setEditingMaterials(true);
                return;
              }
              editMaterialsOf.current = target.id;
              reveal(target.id);
            },
          },
        live && {
          key: "dup",
          label: "Duplicate",
          icon: RING_ICONS.copy,
          hint: "A copy to change",
          needs: ["procedures.manage"],
          run: () => setDialog({ kind: "add", from: target }),
        },
        live && {
          key: "retire",
          label: "Retire",
          icon: RING_ICONS.retire,
          hint: "Takes it off the list",
          needs: ["procedures.manage"],
          danger: true,
          run: () => retire(target),
        },
      ],
    };
  });

  useRingProvider("page", () => ({
    ring: "page.plans",
    title: "Procedures",
    meta: codes === null ? undefined : `${String(active.length)} on the list`,
    actions:
      problem !== null || codes === null
        ? []
        : [
            {
              key: "add",
              label: "Add a procedure",
              icon: RING_ICONS.plus,
              hint: "A new row with its visits",
              needs: ["procedures.manage"],
              run: () => setDialog({ kind: "add", from: null }),
            },
            categories !== null && {
              key: "cats",
              label: "Categories",
              icon: RING_ICONS.tag,
              hint: "Names and colours",
              needs: ["procedures.manage"],
              run: () => setCategoriesOpen(true),
            },
            {
              key: "csv",
              label: "Import a CSV",
              icon: RING_ICONS.upload,
              hint: "Many procedures at once",
              needs: ["procedures.manage"],
              // The same hidden file input the header's and the empty list's buttons click.
              run: () => fileInput.current?.click(),
            },
          ],
  }));

  /** Codes may be null while loading; an empty location is a loaded, empty list. */
  const emptyLocation = codes !== null && codes.length === 0;

  return (
    <div className={styles.page} data-testid="procedures-page">
      <header className={styles.head}>
        {/*
          **Titled for the sidebar item that opens it** (PV7) — "Treatment plans"
          is the procedure list now, each procedure carrying its own visits.
        */}
        <h1 className={styles.title}>Treatment plans</h1>
        {places.many ? (
          <span className={styles.scope} data-testid="pane-scope">
            {places.ids.length} locations
          </span>
        ) : (
          branch !== null && (
            <span className={styles.scope} data-testid="pane-scope">
              {branch.name}
            </span>
          )
        )}
        {canEdit && problem === null && (
          <div className={styles.headActions}>
            {/* Quiet, occasional routes beside the one main action (TR6). */}
            {retiredCount > 0 && (
              <button
                type="button"
                className={styles.quiet}
                aria-pressed={retiredView}
                onClick={() => {
                  setRetiredView((was) => !was);
                  setOpenId(null);
                }}
                data-testid="proc-toggle-retired"
              >
                {retiredView ? "Back to the list" : "Retired procedures"}
              </button>
            )}
            {codes !== null && codes.length > 0 && (
              <button
                type="button"
                className={styles.quiet}
                disabled={busy}
                onClick={() => fileInput.current?.click()}
                data-testid="proc-import-open"
              >
                Import a CSV
              </button>
            )}
            {/* One list for the whole clinic (PG-D2), managed beside the procedures it sorts. */}
            <button
              type="button"
              className={styles.quiet}
              disabled={categories === null}
              onClick={() => setCategoriesOpen(true)}
              data-testid="proc-categories-open"
            >
              Categories
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={busy || codes === null}
              onClick={() => setDialog({ kind: "add", from: null })}
              data-testid="proc-add-open"
            >
              Add a procedure
            </button>
          </div>
        )}
      </header>

      {/*
        **Drawn nothing, driven by the buttons above** — the operating system's
        own *Choose File* widget is not a control this screen offers. Read here,
        parsed on the server: one parser, and the per-row reasons come back from
        the same place that decided them.
      */}
      {canEdit && (
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className={styles.fileInput}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file === undefined) return;
            void file.text().then((csv) =>
              run(
                async () => {
                  setImported(await importCatalogue(csv));
                },
                reload,
                "Import finished.",
              ),
            );
          }}
          data-testid="catalogue-import"
        />
      )}

      {problem !== null ? (
        <div className={styles.noScope}>
          <NoScope what="procedures" reason={problem} />
        </div>
      ) : emptyLocation ? (
        <div className={styles.body}>
          <section className={styles.emptyCard}>
            {canEdit ? (
              <EmptyLocation
                others={(branches ?? []).filter((row) => row.id !== branch?.id)}
                busy={busy}
                onStarter={() => {
                  void run(
                    () => acceptStarterLibrary(),
                    reload,
                    "Added. They are your rows now — edit every one.",
                  );
                }}
                onCopyFrom={(from) => {
                  void run(
                    () => copyCatalogueFrom(from),
                    reload,
                    "Copied. Every price is yours to edit.",
                  );
                }}
                onImport={() => fileInput.current?.click()}
              />
            ) : (
              // Say who fills it, rather than offering three ways in to somebody
              // the server would refuse on every one of them.
              <p className={styles.blank} data-testid="catalogue-empty-readonly">
                No procedures at this location yet. An administrator adds them here.
              </p>
            )}
          </section>
        </div>
      ) : (
        <div className={styles.body}>
          <ShelfList
            testId="catalogue-list"
            head={
              retiredView ? (
                <>
                  <span className={styles.headLabel}>Retired procedures</span>
                  <span className={styles.headCount}>{shown.length}</span>
                </>
              ) : (
                <>
                  <input
                    type="search"
                    className={styles.search}
                    placeholder="Search by name or code…"
                    aria-label="Search procedures"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    data-testid="proc-search"
                  />
                  {/*
                    **The count stays on screen** — it moved off the card's
                    heading to the pager (TR8), and a list that fits on one page
                    draws no pager, so it is said here too.
                  */}
                  {codes !== null && (
                    <span className={styles.headCount} data-testid="proc-count">
                      {query.trim() === ""
                        ? plural(shown.length, "procedure")
                        : `${String(shown.length)} of ${String(active.length)}`}
                    </span>
                  )}
                </>
              )
            }
            columns={
              <div className={styles.columns} aria-hidden="true">
                <span>Procedure</span>
                <span className={styles.columnRight}>Price</span>
              </div>
            }
            rows={rows}
            empty={
              codes === null
                ? "Reading…"
                : query.trim() !== "" && !retiredView
                  ? "No procedure matches that."
                  : "Every procedure here is retired. Retired procedures, above, has them."
            }
            noun="procedures"
            follow={follow}
            resetOn={`${String(activeBranchId)}|${query}|${String(retiredView)}`}
          />

          {open !== null && (
            <ProcedurePanel
              key={open.id}
              locations={
                places.many ? (
                  <CopySwitch
                    copies={copiesOf.get(open.id) ?? []}
                    current={open.id}
                    shown={places}
                    onPick={setOpenId}
                  />
                ) : undefined
              }
              code={open}
              currency={currency}
              canEdit={canEdit}
              busy={busy}
              onClose={() => setOpenId(null)}
              onEdit={() => setDialog({ kind: "edit", code: open })}
              onDuplicate={() => setDialog({ kind: "add", from: open })}
              onRetire={() => retire(open)}
              onRestore={() => {
                // Back in the list, and the list goes with it to where it now is.
                void run(
                  () => updateProcedureCode(open.id, { isActive: true }),
                  reload,
                  "Back in the list.",
                ).then((landed) => {
                  if (!landed) return;
                  setRetiredView(false);
                  reveal(open.id);
                });
              }}
              onScope={(scope) => {
                void run(
                  () => updateProcedureCode(open.id, { scope }),
                  reload,
                  scope === "WHOLE_MOUTH" ? "Done on the whole mouth." : "Done on one tooth.",
                );
              }}
              onMissingTooth={(doneOnMissingTooth) => {
                void run(
                  () => updateProcedureCode(open.id, { doneOnMissingTooth }),
                  reload,
                  doneOnMissingTooth
                    ? "Can be done where a tooth is missing."
                    : "Only on a tooth that is there.",
                );
              }}
              category={categories?.find((row) => row.id === open.procedureCategoryId) ?? null}
              materials={
                hasSupply && (
                  <ProcedureMaterials
                    code={open}
                    currency={currency}
                    materials={materials}
                    items={stock}
                    canEdit={canEdit}
                    canSeeMoney={canSeeMoney}
                    busy={busy}
                    editing={editingMaterials}
                    onEditing={setEditingMaterials}
                    onAdd={(body) =>
                      writeMaterials(() => addProcedureMaterial(open.id, body), "Material added.")
                    }
                    onEdit={(materialId, body) => {
                      void writeMaterials(
                        () => editProcedureMaterial(open.id, materialId, body),
                        "How much it needs is updated.",
                      );
                    }}
                    onRemove={(materialId, name) => {
                      void writeMaterials(
                        () => removeProcedureMaterial(open.id, materialId),
                        `${name} taken off this procedure.`,
                      );
                    }}
                  />
                )
              }
            />
          )}
        </div>
      )}

      {categoriesOpen && categories !== null && (
        <CategoriesDialog
          categories={categories}
          // A removal or rename changes what the list's procedures show too.
          onChanged={() => reloadCategories()}
          onClose={() => setCategoriesOpen(false)}
        />
      )}

      {dialog !== null && (
        <ProcedureDialog
          location={
            dialog.kind === "add" ? (
              <LocationField shown={places} value={addAt} onChange={setAddAt} />
            ) : undefined
          }
          key={dialog.kind === "edit" ? `edit-${dialog.code.id}` : `add-${dialog.from?.id ?? ""}`}
          mode={dialog}
          categories={categories}
          currency={currency}
          busy={busy}
          onClose={() => setDialog(null)}
          onCreate={async (body, visits) => {
            // The new procedure opens beside the list, on whichever page it landed.
            const made: { code: ProcedureCode | null } = { code: null };
            const landed = await run(
              async () => {
                const at = places.many ? (addAt ?? places.active) : null;
                made.code = await atLocation(at, () => createProcedureCode(body));
                if (at !== null) locate(made.code.id, at);
                // A multi-visit procedure's visits, straight after its row (PV1).
                if (visits !== null) await setProcedureVisits(made.code.id, visits);
              },
              reload,
              "Procedure added.",
            );
            if (landed && made.code !== null) {
              setRetiredView(false);
              setQuery("");
              reveal(made.code.id);
            }
            return landed;
          }}
          onEdit={(body, visits) => {
            if (dialog.kind !== "edit") return Promise.resolve(false);
            const id = dialog.code.id;
            return run(
              async () => {
                /**
                 * **The order matters** (PV2). Back to one visit: clear the list
                 * first, so the row's own price and length are editable again
                 * when the `PATCH` sets them. More than one: the `PATCH` first
                 * (it carries no price or length then), then the visits, which
                 * set the row's price and length themselves.
                 */
                if (visits !== null && visits.length === 0) await setProcedureVisits(id, []);
                if (Object.keys(body).length > 0) await updateProcedureCode(id, body);
                if (visits !== null && visits.length > 0) await setProcedureVisits(id, visits);
              },
              reload,
              "Saved.",
            );
          }}
        />
      )}

      {imported !== null && <ImportReport result={imported} onClose={() => setImported(null)} />}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

/**
 * **An empty location keeps its three ways in** (TR6), inside the empty list:
 * the offered standard list (TP10, STORY-115), a copy of another location's,
 * and an import. Typing eighty rows is where a trial gives up (Q2).
 *
 * Copying only fills an empty list — merging raises a question no screen can
 * answer, which is whose price wins — so the offer is gone once there is one.
 */
function EmptyLocation({
  others,
  busy,
  onStarter,
  onCopyFrom,
  onImport,
}: {
  /** Every other location, for copy-from. */
  others: readonly Branch[];
  busy: boolean;
  onStarter: () => void;
  onCopyFrom: (branchId: string) => void;
  onImport: () => void;
}): React.JSX.Element {
  return (
    <div className={styles.empty} data-testid="catalogue-empty">
      <p>
        No procedures yet, so nothing can be picked when booking. Start from the standard list, copy
        another location&rsquo;s, or bring in your own price list.
      </p>
      <div className={styles.ways}>
        <button
          type="button"
          className={styles.primary}
          disabled={busy}
          onClick={onStarter}
          data-testid="catalogue-starter"
        >
          Start from a standard list
        </button>
        {others.length > 0 && (
          <select
            className={`${styles.input} ${styles.copyFrom}`}
            defaultValue=""
            disabled={busy}
            aria-label="Copy another location's list"
            onChange={(event) => {
              const from = event.target.value;
              if (from === "") return;
              event.target.value = "";
              onCopyFrom(from);
            }}
            data-testid="catalogue-copy-from"
          >
            <option value="">Copy another location&rsquo;s list&hellip;</option>
            {others.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          className={styles.ghost}
          disabled={busy}
          onClick={onImport}
          data-testid="catalogue-empty-import"
        >
          Import a CSV
        </button>
      </div>
    </div>
  );
}

/**
 * What an import did (TR6): added, updated, and **each skipped line with its
 * reason** — the outcome, so the admin knows which rows of their file to fix.
 * A bad row never stops the good ones (C1).
 */
function ImportReport({
  result,
  onClose,
}: {
  result: ImportResult;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <Modal label="Import finished" onClose={onClose} testId="import-result">
      <h2 className={styles.dialogTitle}>Import finished</h2>
      <p className={styles.importSummary}>
        {result.added} added, {result.updated} updated
        {result.skipped.length > 0 && `, ${String(result.skipped.length)} skipped`}.
      </p>
      {result.skipped.length > 0 && (
        <ol className={styles.skipped}>
          {result.skipped.map((row) => (
            <li key={row.line} data-testid="import-skipped">
              Line {row.line}
              {row.code !== "" && <> ({row.code})</>} &mdash; {row.reason}
            </li>
          ))}
        </ol>
      )}
      <div className={styles.dialogActions}>
        <button
          type="button"
          className={styles.primary}
          onClick={onClose}
          data-testid="import-dismiss"
        >
          Done
        </button>
      </div>
    </Modal>
  );
}
